import { getDocument } from 'pdfjs-dist/webpack.mjs';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist/webpack.mjs';

const RELEASE_DELAY = 30_000;

interface IPdfCacheEntry {
  loadingTask: PDFDocumentLoadingTask;
  document: Promise<PDFDocumentProxy>;
  referenceCount: number;
  releaseTimer: number | null;
}

export interface IPdfDocumentHandle {
  document: Promise<PDFDocumentProxy>;
  release: () => void;
}

const cache = new Map<string, IPdfCacheEntry>();

const createEntry = (url: string): IPdfCacheEntry => {
  // 스캔 PDF의 JBIG2·JPEG 2000 이미지를 해독할 파일 경로. 누락되면 문서가 열려도 내용은 흰 화면으로 남을 수 있다.
  const wasmUrl = `${process.env.PUBLIC_URL || ''}/static/pdfjs/`;
  const loadingTask = getDocument({ url, wasmUrl });
  const entry: IPdfCacheEntry = {
    loadingTask,
    document: loadingTask.promise,
    referenceCount: 0,
    releaseTimer: null,
  };
  entry.document.catch(() => {
    if (cache.get(url) === entry) cache.delete(url);
    void loadingTask.destroy();
  });
  return entry;
};

export const acquirePdfDocument = (url: string): IPdfDocumentHandle => {
  const entry = cache.get(url) ?? createEntry(url);
  cache.set(url, entry);
  entry.referenceCount += 1;
  if (entry.releaseTimer !== null) {
    window.clearTimeout(entry.releaseTimer);
    entry.releaseTimer = null;
  }

  let released = false;
  return {
    document: entry.document,
    release: () => {
      if (released) return;
      released = true;
      entry.referenceCount -= 1;
      if (entry.referenceCount > 0) return;
      entry.releaseTimer = window.setTimeout(() => {
        if (entry.referenceCount > 0 || cache.get(url) !== entry) return;
        cache.delete(url);
        void entry.loadingTask.destroy();
      }, RELEASE_DELAY);
    },
  };
};
